import {inject, Injectable, OnDestroy} from '@angular/core';
import {MatDialog} from "@angular/material/dialog";
import {AppMsalAuthService} from "../../auth/authentication-service/app-msal-auth.service";
import {interval, Subscription} from "rxjs";
import {TimeoutDialogComponent} from "../../../shared/ui/timeout-dialog/timeout-dialog.component";

@Injectable({
  providedIn: 'root'
})
export class IdleService implements OnDestroy {
  // Constants, time in seconds.
  private readonly TIMEOUT: number = 900;
  private readonly TIMEOUT_DIALOG: number = 780;
  private readonly IDLE_CHECK_INTERVAL: number = 30;

  // Milliseconds Constant
  private readonly MILLISECONDS_IN_SECOND: number = 1000;

  // Injectables
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly authService = inject(AppMsalAuthService);

  private lastActivity?: Date;
  private idleSubscription?: Subscription;

  constructor() {
    this.resetTimer();
    this.startWatching();
  }

  /**
   * Subscribes to idleSubscription, this subscription checks if a user has been inactive for longer than a specified timeout.
   * @private
   */
  private startWatching(): void {
    this.idleSubscription = interval(this.IDLE_CHECK_INTERVAL * this.MILLISECONDS_IN_SECOND) // How often a check should occur to see if a user is idle, every IDLE_CHECK_INTERVAL seconds
      .subscribe(() => {
        const now = new Date();
        if ((now.getTime() - this.lastActivity?.getTime()!) > this.TIMEOUT_DIALOG * this.MILLISECONDS_IN_SECOND) {
          this.openDialog();
        }
        if ((now.getTime() - this.lastActivity?.getTime()!) > this.TIMEOUT * this.MILLISECONDS_IN_SECOND) {
          this.authService.logout();
        }
      });
  }

  /**
   * Resets the lastActivity date to current date.
   */
  public resetTimer(): void {
    this.lastActivity = new Date();
  }

  /**
   * Opens a MatDialog if one is not already open.
   * @private
   */
  private openDialog(): void {
    let alreadyOpened = this.dialog.openDialogs.find(dialog => dialog.componentInstance instanceof TimeoutDialogComponent);
    if (alreadyOpened) {
      // Don't need to open again
      console.log('Idle dialog already opened');
    } else {
      console.log("Opening idle dialog")
      this.dialog.closeAll(); // Close any existing dialogs that happen to be open
      let dialogRef = this.dialog.open(TimeoutDialogComponent);
      dialogRef.afterClosed().subscribe(() => {
        this.resetTimer();
      });
    }
  }

  ngOnDestroy(): void {
    if (this.idleSubscription) {
      this.idleSubscription.unsubscribe();
    }
  }
}
